import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/YoutubeBrandAccounts.graphql';
import { Query, QueryyoutubeBrandAccountsArgs } from '../../__generated__/globalTypes';

export const useYoutubeBrandAccountsQuery = (options?: QueryHookOptions<Query, QueryyoutubeBrandAccountsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryyoutubeBrandAccountsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useYoutubeBrandAccountsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryyoutubeBrandAccountsArgs>
): QueryResult<Query, QueryyoutubeBrandAccountsArgs> & {
  getYoutubeBrandAccounts: LazyQueryExecFunction<Query, QueryyoutubeBrandAccountsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getYoutubeBrandAccounts, result] = useLazyQuery<Query, QueryyoutubeBrandAccountsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getYoutubeBrandAccounts, ...result };
};

export const useYoutubeBrandAccountsPromiseQuery = () => {
  const client = useApolloClient();

  const getYoutubeBrandAccounts = useCallback(
    (options?: Omit<QueryOptions<QueryyoutubeBrandAccountsArgs, Query>, 'query'>) =>
      client.query<Query, QueryyoutubeBrandAccountsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getYoutubeBrandAccounts };
};
// Suspense Query Hooks
export const useYoutubeBrandAccountsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryyoutubeBrandAccountsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryyoutubeBrandAccountsArgs>(QUERY, {
    ...options
  });

  return result;
};
